import api from './api';

const slotService = {
  /**
   * Get all slots
   * @returns {Promise} Array of slots
   */
  getSlots: async () => {
    const response = await api.get('/api/slots');
    return response.data;
  },

  /**
   * Get slot status for a specific date
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Promise} Array of slots with booked count
   */
  getSlotStatus: async (date) => {
    const response = await api.get(`/api/slots/status?date=${date}`);
    return response.data;
  },

  /**
   * Create a new slot
   * @param {Object} slotData - Slot details
   * @returns {Promise} Created slot data
   */
  createSlot: async (slotData) => {
    const response = await api.post('/api/slots', slotData);
    return response.data;
  },

  /**
   * Update slot capacity
   * @param {string} id - Slot MongoDB ID
   * @param {number} maxCapacity - New max capacity
   * @returns {Promise} Updated slot data
   */
  updateSlotCapacity: async (id, maxCapacity) => {
    const response = await api.put(`/api/slots/${id}`, { maxCapacity });
    return response.data;
  },

  /**
   * Update slot details
   * @param {string} id - Slot MongoDB ID
   * @param {Object} updateData - Fields to update
   * @returns {Promise} Updated slot data
   */
  updateSlot: async (id, updateData) => {
    const response = await api.put(`/api/slots/${id}`, updateData);
    return response.data;
  },

  /**
   * Delete a slot
   * @param {string} id - Slot MongoDB ID
   * @returns {Promise} Deletion confirmation
   */
  deleteSlot: async (id) => {
    const response = await api.delete(`/api/slots/${id}`);
    return response.data;
  },

  /**
   * Toggle slot active status
   * @param {string} id - Slot MongoDB ID
   * @returns {Promise} Updated slot data
   */
  toggleSlotActive: async (id) => {
    const response = await api.patch(`/api/slots/${id}/toggle`);
    return response.data;
  },

  /**
   * Seed default slots
   * @returns {Promise} Created slots data
   */
  seedSlots: async () => {
    const response = await api.post('/api/slots/seed');
    return response.data;
  },

  /**
   * Create or update slot override for a specific date
   * @param {string} slotId - Slot MongoDB ID
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {number} capacity - Override capacity
   * @returns {Promise} Override data
   */
  setSlotOverride: async (slotId, date, capacity) => {
    const response = await api.post('/api/slots/override', {
      slotId,
      date,
      capacity
    });
    return response.data;
  },

  /**
   * Get available pickup slots for a date
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Promise} Array of available pickup slots
   */
  getAvailablePickupSlots: async (date) => {
    const response = await api.get(`/api/slots/status?date=${date}`);
    const slots = response.data;
    return slots.filter(slot =>
      slot.slotType === 'Pickup' &&
      slot.isActive !== false &&
      slot.bookedCount < slot.maxCapacity
    );
  },

  /**
   * Get available delivery slots for a date
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Promise} Array of available delivery slots
   */
  getAvailableDeliverySlots: async (date) => {
    const response = await api.get(`/api/slots/status?date=${date}`);
    const slots = response.data;
    return slots.filter(slot =>
      slot.slotType === 'Delivery' &&
      slot.isActive !== false &&
      slot.bookedCount < slot.maxCapacity
    );
  },
};

export default slotService;
